import {
  ChatMessageDto,
  CursorBasedPaginated,
  CustomerChatMessagePaymentDataDto,
  CustomerChatMessageTicketDataDto,
  CustomerChatMessageType,
  CustomerChatMessageVehicleRecommendationCarouselDataDto,
  CustomerChatSenderType,
  MobiTalkSource,
} from '@orcar/common';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getMobiTalkMessages } from '@/apis/resources/mobi-talk.api';
import { mobiTalkSocket } from '@/apis/websocketClient';
import Bubble from '@/components/Bubble';
import ChatInput from '@/components/ChatInput';
import ReservationTicket from '@/components/ReservationTicket';
import TimeStamp from '@/components/TimeStamp';
import VehicleList from '@/components/VehicleList';
import {
  MOBI_TALK_QUERY_KEYS,
  useCreateReservation,
  useGetMobiTalkMessages,
  useSendMobiTalkMessage,
} from '@/hooks/reactQuery/mobi-talk.hook';
import { cn } from '@/utils/cn';
import { getDisplayTime } from '@/utils/mobi-talk';
import CheckoutForm from './CheckoutForm.jsx';

const MESSAGE_LIMIT = 10;

const ChatRoom = ({ roomId }: { roomId: number }) => {
  const queryClient = useQueryClient();

  const observerRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);

  const { pathname } = useLocation();
  const isSure = pathname.includes('mobi-for-sure');

  const [input, setInput] = useState('');
  const [scrollHeightBeforeFetch, setScrollHeightBeforeFetch] =
    useState<number>(0);

  const {
    data: messages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useGetMobiTalkMessages({
    roomId,
    limit: MESSAGE_LIMIT,
  });
  const { mutate: sendMessage } = useSendMobiTalkMessage();
  const { mutate: createReservation } = useCreateReservation();

  const flattenedMessages = useMemo(() => {
    if (!messages?.pages) return [];
    return messages.pages.flatMap((page) => page?.data || []);
  }, [messages?.pages]);

  const sendedSound = new Audio('/sound/send.mp3');
  const paidSound = new Audio('/sound/paid.mp3');
  const ticketSound = new Audio('/sound/ticket.mp3');

  const handleSendMessage = (text: string) => {
    if (!text) return;

    setScrollHeightBeforeFetch(0);
    setInput('');

    sendMessage({
      id: roomId,
      input: {
        text,
        source: isSure ? MobiTalkSource.SURE : MobiTalkSource.D2C,
      },
    });
  };

  const handlePayment = async () => {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        createReservation({
          id: roomId,
        });
        paidSound.play();
        resolve();
      }, 2000);
    });
  };

  useEffect(() => {
    if (roomId) {
      mobiTalkSocket.connect();
      mobiTalkSocket.on('connect', () => {
        mobiTalkSocket.emit('subscribe-chat-room', {
          chatRoomId: roomId,
        });
      });

      if (!mobiTalkSocket.connected) {
        mobiTalkSocket.connect();
      } else {
        mobiTalkSocket.emit('subscribe-chat-room', {
          chatRoomId: roomId,
        });
      }
    }

    return () => {
      mobiTalkSocket.off('connect');
    };
  }, [roomId]);

  useEffect(() => {
    const updateMessageList = async ({
      roomId,
      newData,
      hasToBeLoading,
    }: {
      roomId: number;
      newData: CursorBasedPaginated<ChatMessageDto>;
      hasToBeLoading: boolean;
    }) => {
      queryClient.setQueryData(
        [MOBI_TALK_QUERY_KEYS.MESSAGES, roomId],
        (oldData: InfiniteData<CursorBasedPaginated<ChatMessageDto>>) => {
          if (!oldData) return oldData;

          if (oldData.pages.length === 0)
            return {
              ...oldData,
              pages: [...newData.data],
            };

          const newPages = [...oldData.pages];

          const newMessageIds = new Set(
            newData.data.map((message) => message.id),
          );

          const remainingOldMessages = oldData.pages[0].data.filter(
            (message) => !newMessageIds.has(message.id),
          );

          const isRecommendation =
            newData.data[0].messageType ===
            CustomerChatMessageType.VEHICLE_RECOMMENDATION_CAROUSEL;
          const isPayment =
            newData.data[0].messageType === CustomerChatMessageType.PAYMENT;
          const isTicket =
            newData.data[0].messageType ===
            CustomerChatMessageType.RESERVATION_TICKET;

          const recommendationLoadingText = 'Finding the best cars for you…';
          const paymentLoadingText =
            'Please hold on — your payment screen is loading.';
          const ticketLoadingText = `We're issuing your ticket. Just a moment!`;

          const text = isRecommendation
            ? recommendationLoadingText
            : isPayment
              ? paymentLoadingText
              : isTicket
                ? ticketLoadingText
                : '';

          if (hasToBeLoading) {
            const loadingMessage: ChatMessageDto = {
              id: 0,
              senderType: CustomerChatSenderType.AI_AGENT,
              text,
              messageType: CustomerChatMessageType.PLAIN_TEXT,
              sentAt: new Date(),
              chatRoomId: roomId,
              deletedAt: null,
              createdAt: new Date(),
              updatedAt: new Date(),
              data: {
                text,
              },
            };
            const newFirstPage = [loadingMessage, ...oldData.pages[0].data];

            newPages[0] = {
              ...newPages[0],
              data: newFirstPage,
            };
          } else {
            const newFirstPage = [...newData.data, ...remainingOldMessages];

            newPages[0] = {
              ...newPages[0],
              data: newFirstPage,
            };
          }

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );
    };

    const removeLoadingMessageAndUpdateMessages = ({
      roomId,
      newData,
    }: {
      roomId: number;
      newData: CursorBasedPaginated<ChatMessageDto>;
    }) => {
      queryClient.setQueryData(
        [MOBI_TALK_QUERY_KEYS.MESSAGES, roomId],
        (oldData: InfiniteData<CursorBasedPaginated<ChatMessageDto>>) => {
          if (!oldData) return oldData;

          const newPages = [...oldData.pages];

          const newMessageIds = new Set(
            newData.data.map((message) => message.id),
          );

          const remainingOldMessages = oldData.pages[0].data.filter(
            (message) => !newMessageIds.has(message.id) && message.id !== 0,
          );

          const newFirstPage = [...newData.data, ...remainingOldMessages];

          newPages[0] = {
            ...newPages[0],
            data: newFirstPage,
          };

          return {
            ...oldData,
            pages: newPages,
          };
        },
      );
    };
    const handleNewMessage = async ({
      chatRoomId,
      messageId,
    }: {
      chatRoomId: number;
      messageId: number;
    }) => {
      if (chatRoomId === roomId) {
        const newData = await getMobiTalkMessages(roomId, {
          limit: MESSAGE_LIMIT,
        });

        const newMessage = newData.data.find(
          (message) => message.id === messageId,
        );

        if (!newMessage) return;

        const hasToBeLoading =
          newMessage.messageType ===
            CustomerChatMessageType.RESERVATION_TICKET ||
          newMessage.messageType === CustomerChatMessageType.PAYMENT ||
          newMessage.messageType ===
            CustomerChatMessageType.VEHICLE_RECOMMENDATION_CAROUSEL;

        updateMessageList({
          roomId,
          newData,
          hasToBeLoading,
        });

        if (hasToBeLoading) {
          setTimeout(() => {
            removeLoadingMessageAndUpdateMessages({ roomId, newData });
          }, 5000);
        }
      }
    };

    mobiTalkSocket.on('new-message', handleNewMessage);
    return () => {
      mobiTalkSocket.off('new-message', handleNewMessage);
    };
  }, [roomId, queryClient]);

  useEffect(() => {
    const isScrolling =
      messageContainerRef.current && scrollHeightBeforeFetch > 0;
    const latestMessage = flattenedMessages[0];

    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
      if (messageContainerRef.current) {
        const scrollHeight = messageContainerRef.current.scrollHeight;
        if (behavior === 'auto') {
          messageContainerRef.current.scrollTop = scrollHeight;
        } else {
          messageContainerRef.current.scrollTo({
            top: scrollHeight,
            behavior: 'smooth',
          });
        }
      }
    };

    if (isScrolling) {
      const newScrollHeight = messageContainerRef.current.scrollHeight;
      const heightDifference = newScrollHeight - scrollHeightBeforeFetch;
      messageContainerRef.current.scrollTop = heightDifference;
      setScrollHeightBeforeFetch(0);
    } else {
      scrollToBottom('smooth');
      if (
        latestMessage &&
        latestMessage.messageType === CustomerChatMessageType.PAYMENT
      ) {
        setTimeout(() => {
          scrollToBottom('smooth');
        }, 400);
      }
    }
  }, [messages]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          if (messageContainerRef.current) {
            setScrollHeightBeforeFetch(
              messageContainerRef.current.scrollHeight,
            );
          }
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [observerRef, hasNextPage, fetchNextPage, isFetchingNextPage]);

  return (
    <div className={cn('flex size-full flex-col', isSure && 'bg-sure-010')}>
      <div
        className={cn(
          'relative flex size-full flex-col gap-5 overflow-auto p-10 pb-5',
          isSure && 'px-4 py-7',
        )}
        ref={messageContainerRef}
      >
        {/* 채팅방 */}
        {hasNextPage && <div ref={observerRef} />}
        {flattenedMessages && flattenedMessages.length > 0 && !hasNextPage && (
          <div className='mb-4'>
            <TimeStamp
              time={dayjs(
                flattenedMessages[flattenedMessages.length - 1].sentAt,
              ).format('dddd, MMMM D, YYYY')}
            />
          </div>
        )}
        <div className='flex w-full flex-col-reverse gap-5'>
          {flattenedMessages && flattenedMessages.length > 0 ? (
            flattenedMessages.map((message, index) => {
              const nextMessage =
                index > 0 ? flattenedMessages[index - 1] : null;
              const prevMessage =
                index < flattenedMessages.length - 1
                  ? flattenedMessages[index + 1]
                  : null;

              const currentDate = dayjs(message.sentAt).format('YYYY-MM-DD');
              const prevDate = prevMessage
                ? dayjs(prevMessage.sentAt).format('YYYY-MM-DD')
                : null;
              const showDateChange = prevDate && currentDate !== prevDate;
              const time = dayjs(message.sentAt).format('HH:mm');
              const recommendationMessageIndex = flattenedMessages.findIndex(
                (message) =>
                  message.messageType ===
                  CustomerChatMessageType.VEHICLE_RECOMMENDATION_CAROUSEL,
              );

              const hasUserMessageAfterRecommendation =
                recommendationMessageIndex !== -1 &&
                flattenedMessages.some(
                  (message, idx) =>
                    idx < recommendationMessageIndex &&
                    message.senderType === CustomerChatSenderType.CUSTOMER,
                );

              return (
                <AnimatePresence key={message.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    onAnimationStart={() => {
                      if (
                        message.messageType ===
                        CustomerChatMessageType.RESERVATION_TICKET
                      ) {
                        ticketSound.play();
                      } else {
                        sendedSound.play();
                      }
                    }}
                  >
                    {message.messageType ===
                      CustomerChatMessageType.PLAIN_TEXT && (
                      <Bubble
                        userText={message.text}
                        time={getDisplayTime(message, nextMessage)}
                        senderType={message.senderType}
                        isSure={isSure}
                        isLoadingMode={message.id === 0}
                      />
                    )}
                    {message.messageType ===
                      CustomerChatMessageType.VEHICLE_RECOMMENDATION_CAROUSEL && (
                      <>
                        <VehicleList
                          data={
                            message.data as CustomerChatMessageVehicleRecommendationCarouselDataDto
                          }
                          time={time}
                          isSure={isSure}
                          isDisabled={hasUserMessageAfterRecommendation}
                          onClick={(vehicleModelName) => {
                            sendMessage({
                              id: roomId,
                              input: {
                                text: vehicleModelName,
                                source: isSure
                                  ? MobiTalkSource.SURE
                                  : MobiTalkSource.D2C,
                              },
                            });
                          }}
                        />
                      </>
                    )}
                    {message.messageType === CustomerChatMessageType.PAYMENT &&
                      (message.data as CustomerChatMessagePaymentDataDto)
                        .status !== 'PAID' && (
                        <CheckoutForm
                          onSubmit={handlePayment}
                          time={time}
                          isSure={isSure}
                          price={
                            (message.data as CustomerChatMessagePaymentDataDto)
                              .price
                          }
                        />
                      )}
                    {message.messageType ===
                      CustomerChatMessageType.RESERVATION_TICKET && (
                      <ReservationTicket
                        data={message.data as CustomerChatMessageTicketDataDto}
                        time={time}
                        isSure={isSure}
                      />
                    )}
                  </motion.div>

                  {showDateChange && (
                    <div className='mt-5'>
                      <TimeStamp
                        time={dayjs(message.sentAt).format(
                          'dddd, MMMM D, YYYY',
                        )}
                      />
                    </div>
                  )}
                </AnimatePresence>
              );
            })
          ) : isLoading ? (
            <div className='py-4 text-center text-[14px] font-[500] leading-[20px] text-gray-500'>
              loading...
            </div>
          ) : null}
        </div>
      </div>
      {/* 채팅 인풋 */}

      <div className={cn('p-10 pt-0', isSure && 'bg-grey-005 p-3')}>
        <ChatInput
          isSure={isSure}
          className='w-full'
          placeholder='Ask me anything about your trip...'
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
          }}
          onSubmit={() => handleSendMessage(input)}
        />
      </div>
    </div>
  );
};

export default ChatRoom;
